import { useState } from "react";
import {
  answersOf,
  askedWhole,
  formComplete,
  problemsOf,
  type FieldValue,
  type FormDraft,
} from "@libs/buy-on-behalf-client/purchase";
import type { AttemptAction, AttemptScreen, Answers } from "@libs/platform-wire/purchase";
import { CONTROL_HEIGHT, button, text } from "@libs/checkout-web/ui";
import { LiveBrowserView } from "../live-browser";
import { PurchaseSection } from "./fields";

type Ask = Extract<AttemptScreen, { kind: "ask" }>;
type ConfirmTotal = Extract<AttemptScreen, { kind: "confirm_total" }>;
type LiveView = Extract<AttemptScreen, { kind: "live_view" }>;

/**
 * What the seller asks while its checkout waits (the code it sent the
 * buyer): the view's hint, its fields as the purchase's form draws them,
 * and its submit; once answered, it waits for the seller to read it. A
 * refused answer (`retry`) clears the form for the next. Resend actions
 * are drawn under it by `AttemptActions`.
 */
export function AskScreen(props: { screen: Ask; disabled: boolean; onAnswer: (answers: Answers) => void }) {
  // A new step is a new form: nothing typed for the last is kept.
  return <AskForm key={`${props.screen.sequence}:${props.screen.retry}`} {...props} />;
}

function AskForm({ screen, disabled, onAnswer }: { screen: Ask; disabled: boolean; onAnswer: (answers: Answers) => void }) {
  const [draft, setDraft] = useState<FormDraft>({});
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const waiting = disabled || screen.answered;
  const answers = answersOf(screen.sections, draft, {});
  const ready = !waiting && formComplete(screen.sections, answers);
  const problems = problemsOf(screen.sections, answers);
  const all = askedWhole(screen.sections);
  return (
    <form
      data-testid="attempt-ask"
      style={{ display: "grid", gap: "var(--lc-space)" }}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) onAnswer(answers);
      }}
    >
      {/* The hint says a refused code was refused (`retry`). */}
      <p role={screen.retry ? "alert" : undefined} style={{ ...text.hint, margin: 0, textAlign: "center" }}>
        {screen.hint}
      </p>
      {screen.sections.map((section) => (
        <PurchaseSection
          key={section.id}
          section={section}
          sections={screen.sections}
          draft={draft}
          context={{}}
          problems={problems}
          touched={touched}
          asked={all}
          editing={false}
          disabled={waiting}
          onChange={(fieldId: string, value: FieldValue) => setDraft((previous) => ({ ...previous, [fieldId]: value }))}
          onBlur={(fieldId) => setTouched((previous) => new Set([...previous, fieldId]))}
          onEdit={() => undefined}
        />
      ))}
      <button
        type="submit"
        disabled={!ready}
        style={{ ...button.primary, minHeight: CONTROL_HEIGHT, opacity: ready ? 1 : 0.6 }}
      >
        {screen.answered ? "Checking" : screen.submit}
      </button>
    </form>
  );
}

/**
 * The exact total of a seller that adds tax at checkout, shown while its
 * last page waits before Pay: the hint, then pay it or refuse, which stops
 * the purchase before Pay. Once answered, both wait for the seller.
 */
export function ConfirmTotalScreen({
  screen,
  disabled,
  onAnswer,
}: {
  screen: ConfirmTotal;
  disabled: boolean;
  onAnswer: (accept: boolean) => void;
}) {
  const waiting = disabled || screen.answered;
  return (
    <section aria-label="Confirm your total" data-testid="total-prompt" style={{ display: "grid", gap: "var(--lc-space)" }}>
      <p style={{ ...text.hint, margin: 0, textAlign: "center" }}>{screen.hint}</p>
      <button
        type="button"
        disabled={waiting}
        onClick={() => onAnswer(true)}
        style={{ ...button.primary, minHeight: CONTROL_HEIGHT, opacity: waiting ? 0.6 : 1 }}
      >
        {screen.accept}
      </button>
      <button
        type="button"
        disabled={waiting}
        onClick={() => onAnswer(false)}
        style={{ ...button.link, justifySelf: "center", opacity: waiting ? 0.6 : 1 }}
      >
        {screen.refuse}
      </button>
    </section>
  );
}

/**
 * A check for the buyer in the browser paying: their bank's (3-D Secure)
 * or the seller's robot tick box. The buyer answers it there, in place,
 * under the view's hint; the keyboard only where the check may need typing.
 */
export function LiveViewScreen({ screen }: { screen: LiveView }) {
  return (
    <div data-testid="attempt-live-view" style={{ display: "grid", gap: "var(--lc-space)" }}>
      <p style={{ ...text.hint, margin: 0, textAlign: "center" }}>{screen.hint}</p>
      <LiveBrowserView
        host={screen.host}
        keyboard={screen.keyboard}
        style={{
          // Room for the whole frame on a phone: what the box cuts off is
          // out of reach.
          maxHeight: "min(78dvh, 760px)",
          borderRadius: "var(--lc-radius-sm)",
          border: "var(--lc-border-width) var(--lc-border-style) var(--lc-input-border)",
        }}
      />
    </div>
  );
}

/**
 * Under the status, never instead of it: the view's note, and what the
 * buyer can do now. A link leaves for the wallet or the seller's site;
 * every other action is the checkout's own.
 */
export function AttemptActions({
  note,
  actions,
  disabled,
  onAction,
  onOpenWallet,
}: {
  note: string | null;
  actions: readonly AttemptAction[];
  disabled: boolean;
  onAction: (action: AttemptAction) => void;
  /** Inside the Lissen app: its wallet, opened in place. */
  onOpenWallet?: () => void;
}) {
  const quiet = actions.every((action) => action.kind === "stop" || action.kind === "resend");
  return (
    <div style={{ display: "grid", gap: "calc(var(--lc-space) * 1.25)", textAlign: "center" }}>
      {note ? <p style={{ ...text.hint, margin: 0 }}>{note}</p> : null}
      {actions.length ? (
        <div style={{ display: "grid", gap: "var(--lc-space)", justifyItems: quiet ? "center" : undefined }}>
          {actions.map((action) => (
            <ActionControl
              key={`${action.kind}:${action.label}`}
              action={action}
              disabled={disabled}
              onAction={onAction}
              onOpenWallet={onOpenWallet}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ActionControl({
  action,
  disabled,
  onAction,
  onOpenWallet,
}: {
  action: AttemptAction;
  disabled: boolean;
  onAction: (action: AttemptAction) => void;
  onOpenWallet?: () => void;
}) {
  const own = (style: typeof button.secondary) => (
    <button type="button" disabled={disabled} onClick={() => onAction(action)} style={style}>
      {action.label}
    </button>
  );
  const link = (url: string) => (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...button.secondary, textDecoration: "none" }}>
      {action.label}
    </a>
  );
  switch (action.kind) {
    case "wallet":
      return onOpenWallet ? (
        <button type="button" onClick={onOpenWallet} style={button.secondary}>
          {action.label}
        </button>
      ) : (
        link(action.url)
      );
    case "continue_on_seller":
      return link(action.url);
    case "stop":
    case "resend":
      return own({ ...button.link, textDecoration: "none" });
    case "stop_again":
    case "back_to_checkout":
    case "reselect":
    case "checked_email":
      return own(button.secondary);
  }
}
